package com.example.socialmediaapp.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import com.example.socialmediaapp.network.ApiUser
import com.example.socialmediaapp.network.AuthRepository
import com.example.socialmediaapp.network.ChatRepository
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class, FlowPreview::class)
@Composable
fun NewChatScreen(
    authRepository: AuthRepository? = null,
    chatRepository: ChatRepository? = null,
    onChatCreated: (chatId: String) -> Unit = {},
    onBack: () -> Unit
) {
    val scope = rememberCoroutineScope()
    var searchQuery by remember { mutableStateOf("") }
    var users by remember { mutableStateOf<List<ApiUser>>(emptyList()) }
    var isLoading by remember { mutableStateOf(false) }
    var isCreating by remember { mutableStateOf(false) }

    val queryFlow = remember { MutableStateFlow("") }
    LaunchedEffect(Unit) {
        queryFlow.debounce(400).distinctUntilChanged().collect { q ->
            if (q.isBlank() || authRepository == null) { users = emptyList(); return@collect }
            isLoading = true
            authRepository.searchUsers(q).onSuccess { users = it }
            isLoading = false
        }
    }
    LaunchedEffect(searchQuery) { queryFlow.value = searchQuery }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("New Chat") },
                navigationIcon = {
                    IconButton(onClick = onBack) { Icon(Icons.Default.ArrowBack, "Back") }
                }
            )
        }
    ) { padding ->
        Column(Modifier.fillMaxSize().padding(padding)) {
            TextField(
                value = searchQuery,
                onValueChange = { searchQuery = it },
                modifier = Modifier.fillMaxWidth().padding(16.dp),
                placeholder = { Text("Search by username") },
                leadingIcon = { Icon(Icons.Default.Search, null) },
                trailingIcon = {
                    if (searchQuery.isNotEmpty()) {
                        IconButton(onClick = { searchQuery = "" }) { Icon(Icons.Default.Close, "Clear") }
                    }
                },
                singleLine = true,
                shape = MaterialTheme.shapes.large
            )

            when {
                isCreating -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        CircularProgressIndicator()
                        Spacer(Modifier.height(8.dp))
                        Text("Starting chat...", style = MaterialTheme.typography.bodyMedium)
                    }
                }
                isLoading -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
                searchQuery.isBlank() -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(Icons.Default.Chat, null, Modifier.size(64.dp), tint = MaterialTheme.colorScheme.primary.copy(alpha = 0.5f))
                        Spacer(Modifier.height(16.dp))
                        Text("Search for users to chat with", style = MaterialTheme.typography.titleMedium)
                    }
                }
                users.isEmpty() -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(Icons.Default.PersonSearch, null, Modifier.size(64.dp), tint = MaterialTheme.colorScheme.onSurfaceVariant)
                        Spacer(Modifier.height(16.dp))
                        Text("No users found", style = MaterialTheme.typography.titleMedium)
                    }
                }
                else -> LazyColumn {
                    items(users, key = { it.id }) { user ->
                        ListItem(
                            headlineContent = { Text(user.username) },
                            supportingContent = user.bio?.let { { Text(it, maxLines = 1) } },
                            leadingContent = {
                                Box(
                                    modifier = Modifier.size(48.dp).clip(CircleShape)
                                        .background(MaterialTheme.colorScheme.primaryContainer),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        user.username.firstOrNull()?.uppercase() ?: "?",
                                        style = MaterialTheme.typography.titleMedium,
                                        color = MaterialTheme.colorScheme.onPrimaryContainer
                                    )
                                }
                            },
                            modifier = Modifier.clickable {
                                scope.launch {
                                    isCreating = true
                                    chatRepository?.createChat(
                                        name = user.username,
                                        type = "direct",
                                        participantIds = listOf(user.id)
                                    )?.fold(
                                        onSuccess = { chatId -> onChatCreated(chatId) },
                                        onFailure = { isCreating = false }
                                    ) ?: isCreating.also { isCreating = false }
                                }
                            }
                        )
                        HorizontalDivider()
                    }
                }
            }
        }
    }
}
